/**
 * Pure helper that classifies a column's DEFAULT-expression text into a
 * sequence reference, when one is present. Used by per-column annotations to
 * surface a "Sequence" chip on the column row.
 *
 * Strict scope per CLAUDE.md "no new regex parsing" guidance — this file only
 * recognises the 3 idioms we actually surface in the UI today:
 *   - pgsql `nextval('schema.seq')` / `nextval('seq')`
 *   - mssql `NEXT VALUE FOR schema.seq`
 *   - mysql / sqlite implicit `serial` / `autoincrement` shorthand
 *     (defaultText + identity-style hints — those are already covered by the
 *     IDENTITY badge so we only return a sequence ref when the source text
 *     literally names a sequence the user can navigate to).
 *
 * Slice H2.
 */
import type { SequenceDefaultRef } from './types';

const NEXTVAL_RE = /\bnextval\s*\(\s*'([^']+)'/i;
const NEXT_VALUE_FOR_RE = /\bNEXT\s+VALUE\s+FOR\s+([a-zA-Z0-9_."[\]`]+)/i;

export function sequenceDefaultFromText(
  defaultText: string | null | undefined,
): SequenceDefaultRef | null {
  if (!defaultText) return null;
  const trimmed = defaultText.trim();
  if (trimmed.length === 0) return null;

  const nextval = NEXTVAL_RE.exec(trimmed);
  if (nextval && nextval[1]) {
    return { sequenceName: nextval[1], idiom: 'nextval' };
  }

  const nextValueFor = NEXT_VALUE_FOR_RE.exec(trimmed);
  if (nextValueFor && nextValueFor[1]) {
    return { sequenceName: nextValueFor[1], idiom: 'next-value-for' };
  }

  return null;
}
